/**
 * Utilerías
 */
//Importar el espacio de almacenamiento de base de datos firestore
import { pacientesStore,usersCollection } from '../utils/firebase';
import { obtenerNivelUsuario } from '../utils/perfiles';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

/* ── Registro de auditoría ─────────────────────────────────────
   VersionRegistro = 2 identifica a los usuarios creados con la versión
   nueva (perfiles agenda / consulta / mesa_ayuda). Los de la versión
   anterior no tienen este campo.
   Usuario de creación / modificación = nombre del usuario logueado. */
export const VERSION_REGISTRO = 2

const usuarioSesion = () => ({
    nombre: sessionStorage.getItem('name') || 'Desconocido',
    id:     sessionStorage.getItem('id')   || '',
})

const ahora = () => firebase.firestore.FieldValue.serverTimestamp()

/** Campos de auditoría para una modificación. */
export const auditoriaModificacion = () => {
    const u = usuarioSesion()
    return {
        FechaModificacion:     ahora(),
        UsuarioModificacion:   u.nombre,
        UsuarioModificacionId: u.id,
    }
}

/** Campos de auditoría para una creación (incluye la modificación inicial). */
const auditoriaCreacion = () => {
    const u = usuarioSesion()
    return {
        VersionRegistro:   VERSION_REGISTRO,
        Origen:            'agenda-web',
        FechaCreacion:     ahora(),
        UsuarioCreacion:   u.nombre,
        UsuarioCreacionId: u.id,
        ...auditoriaModificacion(),
    }
}

/**
 * Registra el acceso exitoso de un usuario a la Agenda Quirúrgica.
 * Usa campos propios de la Agenda para no mezclarse con otros sistemas que
 * compartan la colección, y NO toca FechaModificacion (entrar no es modificar).
 * Si falla, no interrumpe el inicio de sesión.
 * @param {String} id Id del usuario
 */
export const registrarAcceso = async (id) => {
    if (!id) return
    try {
        await pacientesStore.collection(usersCollection).doc(id).update({
            UltimoAccesoAgenda: ahora(),
            TotalAccesosAgenda: firebase.firestore.FieldValue.increment(1),
        })
    } catch (error) {
        console.warn('No se pudo registrar el último acceso:', error)
    }
}

/**
 * Consulta la contraseña de un usuario bajo demanda (solo Mesa de Ayuda).
 * La contraseña no viaja en el listado: se lee aquí del documento y cada
 * consulta queda registrada (quién y cuándo). No toca FechaModificacion.
 * @param {String} id Id del usuario consultado
 * @returns {Promise<String|null>} la contraseña, o null si no se pudo leer
 */
export const consultarContrasena = async (id) => {
    try {
        const ref  = pacientesStore.collection(usersCollection).doc(id)
        const snap = await ref.get()
        if (!snap.exists) return null
        const u = usuarioSesion()
        await ref.update({
            UltimaConsultaPass:          ahora(),
            UsuarioConsultaPass:         u.nombre,
            UsuarioConsultaPassId:       u.id,
            TotalConsultasPass:          firebase.firestore.FieldValue.increment(1),
        })
        return snap.data().Pass ?? ''
    } catch (error) {
        console.error('No se pudo consultar la contraseña:', error)
        return null
    }
}

/** Convierte un Timestamp de Firestore (o fecha) a Date; null si no hay. */
const aFecha = (v) => (v && typeof v.toDate === 'function') ? v.toDate() : (v ? new Date(v) : null)

/**
* Función que solicita el registro de un nuevo usuario a la colección de datos
* @param {String} level Recibe el nivel de acceso asignado al usuario puede ser (administrador,soporte,usuario)
* @param {String} name Recibe el nombre del usuario a registrar
* @param {Object} unidadHospitalaria Recibe { hospital, calendar } de la unidad asignada al usuario
* @param {String} pass Recibe el password de acceso al sistema del usuario
* @param {String} user Recibe el nick de acceso al sistema del usuario
*/
 export const createUser = async (level,name,unidadHospitalaria,pass,user) => {
    try {
        await pacientesStore
        .collection(usersCollection)
        .add({
            Activo:             true,
            Permiso:            level,
            Nombre:             name,
            // Se guarda como objeto {hospital, calendar} — así es como
            // Login.js lo consume (user.UnidadHospitalaria.hospital /
            // .calendar). Antes se guardaba como texto plano, lo que
            // rompía el inicio de sesión de cualquier usuario creado así.
            UnidadHospitalaria: unidadHospitalaria,
            Pass:               pass,
            Nick:               user,
            Systems: [
                {
                    id: 2,
                    active: true
                }
            ],
            ...auditoriaCreacion(),
        })
        return "success"
    } catch (error) {
        return error
    }
}

/**
* Función que solicita la actualización de un usuario a la colección de datos
* @param {String} id Recibe el identificador unico del usuario
* @param {String} level Recibe el nivel de acceso asignado al usuario puede ser (administrador,soporte,usuario)
* @param {String} name Recibe el nombre del usuario a registrar
* @param {Object} unidadHospitalaria Recibe { hospital, calendar } de la unidad asignada al usuario
* @param {String} pass Recibe el password de acceso al sistema del usuario
* @param {String} user Recibe el nick de acceso al sistema del usuario
* @param {String} activo Recibe el estado activo del usuario puede ser (1,0)
*/
export const updateUser = async (id,level,name,unidadHospitalaria,pass,user,activo) => {
    try {
        await pacientesStore
        .collection(usersCollection)
        .doc(id)
        .update({
            Activo:             activo,
            Permiso:            level,
            Nombre:             name,
            UnidadHospitalaria: unidadHospitalaria,
            Pass:               pass,
            Nick:               user,
            ...auditoriaModificacion(),
        })
        return "success"
      } catch (error) {
        return error
      }
}

/**
* Verifica si ya existe un usuario con ese Nick, para evitar duplicados
* (Login.js filtra por Nick — si hay más de uno, la validación de
* contraseña se vuelve ambigua).
* @param {String} nick Nick a validar
* @returns {Boolean} true si ya existe un usuario con ese Nick
*/
export const existeNick = async (nick) => {
    const snap = await pacientesStore
        .collection(usersCollection)
        .where('Nick', '==', nick)
        .get()
    return !snap.empty
}

/**
 * Escucha en tiempo real la lista de usuarios con acceso registrado a la
 * Agenda Quirúrgica (sistema 2). Nunca expone la contraseña.
 * @param {Function} onData  Recibe el arreglo de usuarios
 * @param {Function} onError Recibe el error
 * @returns {Function} unsubscribe
 */
export const escucharUsuariosAgenda = (onData, onError) =>
    pacientesStore
        .collection(usersCollection)
        .onSnapshot(
            (snap) => {
                const lista = []
                snap.forEach((doc) => {
                    const d = doc.data()
                    const sistema = (d.Systems || []).find((s) => s.id === 2 || s.id === '2')
                    if (!sistema) return
                    lista.push({
                        id:        doc.id,
                        Nombre:    d.Nombre || '',
                        Nick:      d.Nick || '',
                        Permiso:   obtenerNivelUsuario(d),
                        Unidad:    d.UnidadHospitalaria?.hospital || '',
                        Activo:    !!d.Activo,
                        AccesoAgenda: !!sistema.active,
                        Systems:   d.Systems || [],
                        // Registro nuevo = creado con la versión actual (o por el backend nuevo)
                        EsNuevo:             (d.VersionRegistro || 0) >= VERSION_REGISTRO || !!d.FechaCreacion,
                        FechaCreacion:       aFecha(d.FechaCreacion),
                        UsuarioCreacion:     d.UsuarioCreacion || d.CreadoPor || '',
                        FechaModificacion:   aFecha(d.FechaModificacion),
                        UsuarioModificacion: d.UsuarioModificacion || '',
                        UltimoAcceso:        aFecha(d.UltimoAccesoAgenda),
                        TotalAccesos:        d.TotalAccesosAgenda || 0,
                        UltimaConsultaPass:  aFecha(d.UltimaConsultaPass),
                        UsuarioConsultaPass: d.UsuarioConsultaPass || '',
                    })
                })
                onData(lista)
            },
            (err) => onError && onError(err)
        )

/**
 * Activa o inactiva el acceso de un usuario a la Agenda Quirúrgica.
 * Solo modifica el sistema 2, para no afectar el acceso del usuario a
 * otros sistemas que compartan la colección. Al activar también se
 * asegura que el usuario quede Activo.
 * Si el usuario es de la versión anterior y se ACTIVA, queda marcado como de la
 * versión actual (VersionRegistro = 2) y se registra quién y cuándo lo actualizó.
 * No se inventa FechaCreacion: se conserva vacía porque se desconoce la original.
 * @param {Object}  usuario Objeto devuelto por escucharUsuariosAgenda
 * @param {Boolean} activo  true = activar, false = inactivar
 */
export const setAccesoAgenda = async (usuario, activo) => {
    try {
        const Systems = (usuario.Systems || []).map((s) =>
            (s.id === 2 || s.id === '2') ? { ...s, active: activo } : s
        )
        const cambios = { Systems, ...auditoriaModificacion() }
        if (activo) cambios.Activo = true
        if (activo && !usuario.EsNuevo) {
            const u = usuarioSesion()
            Object.assign(cambios, {
                VersionRegistro:               VERSION_REGISTRO,
                VersionAnteriorActualizada:    true,
                FechaActualizacionVersion:     ahora(),
                UsuarioActualizacionVersion:   u.nombre,
                UsuarioActualizacionVersionId: u.id,
            })
        }
        await pacientesStore.collection(usersCollection).doc(usuario.id).update(cambios)
        return 'success'
    } catch (error) {
        return error
    }
}

/**
 * Cambia el perfil (Permiso) de un usuario.
 * @param {String} id     Id del usuario
 * @param {String} perfil agenda | consulta | mesa_ayuda
 */
export const setPerfilUsuario = async (id, perfil) => {
    try {
        await pacientesStore.collection(usersCollection).doc(id).update({ Permiso: perfil, ...auditoriaModificacion() })
        return 'success'
    } catch (error) {
        return error
    }
}
